import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { isAxiosError } from "axios";
import { Steps } from "./config";
import {
  RequestActivePricesParams,
  CreateOfferFieldsType,
  CreateOfferResponse,
  StepsBaseType,
  StepsType,
  StepType,
  DisplayOfferFieldsType,
  SingleServiceType,
  MacrosServiceType,
  SupplierPriceType,
  RequestMarginParams,
  MarginResponse,
} from "./types";
import { RootState, AppThunk } from "../../store";
import { createOfferApi, getOfferApi, updateOfferApi } from "../offerList/api";
import {
  calculateMarginApi,
  getActivePricesApi,
  getMacrosPricesApi,
  getPassivePricesApi,
} from "./api";
import {
  normalizeOfferResponse,
  updateStepsWithOfferData,
} from "./helpers/normalize";
import { OfferResponseServices } from "../offerView/types";
import { FlowOfGoodsEnum, IncotermEnum } from "@helpers/incoterm";
import { getErrorMessageFromResponse } from "@helpers/get-error-message";
import { fetchOfferServices } from "@pages/offerView/offerSlice";

const notRequiredFields = [
  "comment",
  "place_of_delivery_id",
  "place_of_receipt_id",
  "place_of_receipt",
  "place_of_delivery",
  "loading_port",
  "discharge_port",
  "customer",
  "terms_of_payment",
  "assigned_office",
  "terminals_details",
];

const requiredIncoterms = [
  IncotermEnum.EXW,
  IncotermEnum.FCA,
  IncotermEnum.FOB,
];

export const validateStepFields = (fieldsValues: Record<string, any>) => {
  const flowOfGoods = fieldsValues?.flow_of_goods;
  const incoterm = fieldsValues?.incoterm;
  const isLoadingPortRequired =
    flowOfGoods &&
    flowOfGoods.toLowerCase() === FlowOfGoodsEnum.IMPORT.toLowerCase() &&
    incoterm &&
    requiredIncoterms.includes(incoterm as IncotermEnum);

  const prices = fieldsValues?.prices;
  if (Array.isArray(prices) && prices.length > 0) {
    const hasInvalidServices = prices.some(
      (service: any) =>
        service.active_price?.is_valid === false ||
        service.passive_prices?.some((price: any) => price.is_valid === false),
    );
    if (hasInvalidServices) {
      return false;
    }
  }

  return Object.entries(fieldsValues || {}).every(([key, value]) => {
    if (
      (key === "loading_port" || key === "loading_port_id") &&
      !isLoadingPortRequired
    ) {
      return true;
    }
    if (
      (fieldsValues?.type_of_shipment === "Bulk" &&
        key === "container_shipment_type") ||
      notRequiredFields.includes(key)
    ) {
      return true;
    }
    if (Array.isArray(value) && !value.length) return false;
    return value !== null && value !== undefined && value !== "";
  });
};

const initialStepsState: StepsBaseType = Steps.sort(
  (a: { order: number }, b: { order: number }) => a.order - b.order,
);

const initialState: StepsType = {
  steps: initialStepsState,
  offer: null,
  type: "",
  services: {
    singleServices: null,
    loading: false,
    error: null,
    total: 0,
  },
  macros: {
    macrosServices: null,
    loading: false,
    error: null,
    total: 0,
  },
  suppliers: {
    services: null,
    loading: false,
    error: null,
    total: 0,
  },
  margin: {
    loading: false,
    error: null,
  },
  selectedService: null,
  selectedPackage: null,
  options: null,
  loading: false,
  error: null,
  success: null,
  notFoundError: null,
  offerFilled: false,
};

const steps = createSlice({
  name: "steps",
  initialState: initialState,
  reducers: {
    stepsInit(state, action: PayloadAction<number>) {
      const targetStepOrder = action.payload ?? 0;
      const current = state.steps.find((item) => item.active);

      if (current) {
        current.completed = current.validated;
        current.active = false;
      }

      state.steps = state.steps.map((step) => ({
        ...step,
        active: step.order === targetStepOrder,
      }));
    },
    setStepValidated(
      state,
      {
        payload,
      }: PayloadAction<{
        id: string | undefined;
        fieldsValues: DisplayOfferFieldsType;
      }>,
    ) {
      const { id, fieldsValues } = payload;
      const step = state.steps.find((item) => item.id === id) || state.steps[0];
      step.validated = validateStepFields(fieldsValues);
      step.fieldsValues = {
        ...step.fieldsValues,
        ...fieldsValues,
      };
      if (!step.validated) step.completed = false;
      state.options = state.steps.reduce(
        (acc, step) => ({ ...acc, ...step.fieldsValues }),
        {},
      );
      const { loading_port, discharge_port, ...rest } = state.options;
      state.options = rest;
    },
    setNextStep(state) {
      const current = state.steps.find((item) => item.active) || state.steps[0];
      if (current.validated) current.completed = true;
      state.steps[current.order].active = false;
      if (state.steps[current.order + 1]) {
        state.steps[current.order + 1].active = true;
      }
    },
    setPrevStep(state) {
      const current = state.steps.find((item) => item.active) || state.steps[0];
      if (current.validated) current.completed = true;
      state.steps[current.order].active = false;
      state.steps[current.order - 1].active = true;
    },
    getOfferStart(state) {
      state.loading = true;
      state.error = null;
    },
    getOfferSuccess(
      state,
      action: PayloadAction<{ offer: CreateOfferResponse; type: string }>,
    ) {
      const { offer, type } = action.payload;
      state.offer = offer;
      state.offer_id = offer.id;
      const updatedSteps = updateStepsWithOfferData(state.steps, offer);

      updatedSteps.forEach((step) => {
        step.validated = validateStepFields(step.fieldsValues || {});
        step.completed = true;
        state.options = { ...state.options, ...step.fieldsValues };
        const { loading_port, discharge_port, ...rest } = state.options;
        state.options = rest;
      });

      state.steps = updatedSteps;
      state.type = type;
      state.loading = false;
      state.offerFilled = true;
    },
    getOfferFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setNotFoundError(state, action: PayloadAction<string>) {
      state.notFoundError = action.payload;
      state.loading = false;
    },
    submitOfferSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.success = action.payload;
    },
    hideWarning(state) {
      state.error = null;
      state.success = null;
    },
    cancelOffer() {
      return initialState;
    },
    getServicesStart(state) {
      state.services.loading = true;
      state.services.error = null;
    },
    getServicesFailure(state, action: PayloadAction<string>) {
      state.services.loading = false;
      state.services.error = action.payload;
    },
    getServicesSuccess(
      state,
      action: PayloadAction<{ services: SingleServiceType[]; total: number }>,
    ) {
      state.services.loading = false;
      state.services.singleServices = action.payload.services;
      state.services.total = action.payload.total;
    },
    getMacrosStart(state) {
      state.macros.loading = true;
      state.macros.error = null;
    },
    getMacrosFailure(state, action: PayloadAction<string>) {
      state.macros.loading = false;
      state.macros.error = action.payload;
    },
    getMacrosSuccess(
      state,
      action: PayloadAction<{ services: MacrosServiceType[]; total: number }>,
    ) {
      state.macros.loading = false;
      state.macros.macrosServices = action.payload.services;
      state.macros.total = action.payload.total;
    },
    getSuppliersListStart(state) {
      state.suppliers.services = null;
      state.suppliers.loading = true;
      state.suppliers.error = null;
    },
    getSuppliersListFailure(state, action: PayloadAction<string>) {
      state.suppliers.loading = false;
      state.suppliers.error = action.payload;
    },
    getSuppliersListSuccess(
      state,
      action: PayloadAction<{
        items: SupplierPriceType[];
        total: number;
      }>,
    ) {
      state.suppliers.loading = false;
      state.suppliers.services = action.payload.items;
      state.suppliers.total = action.payload.total;
    },
    getAddedServicesSuccess(
      state,
      action: PayloadAction<OfferResponseServices>,
    ) {
      const activeServices = action.payload.items.map((service) => ({
        association_id: service.id,
        ...service,
      }));

      const has_invalid = action.payload.meta?.has_invalid || false;

      state.steps[2].fieldsValues = {
        ...state.steps[2].fieldsValues,
        prices: activeServices,
      };

      state.options = { ...state.options, prices: activeServices, has_invalid };

      state.steps[2].validated = validateStepFields(
        state.steps[2].fieldsValues,
      );
    },
    getSupplierMarginStart(state) {
      state.margin.loading = true;
      state.margin.error = null;
    },
    getSupplierMarginFailure(state, action: PayloadAction<string>) {
      state.margin.loading = false;
      state.margin.error = action.payload;
    },
    getSupplierMarginSuccess(
      state,
      action: PayloadAction<{ margins: MarginResponse; supplierId: number }>,
    ) {
      state.margin.loading = false;
      const service = state.suppliers.services?.find(
        (service) => service.id === action.payload.supplierId,
      );
      if (service) {
        service.margin = action.payload.margins;
      }
    },
    setServiceField(
      state,
      action: PayloadAction<{
        row: SingleServiceType;
        parentId?: number;
      }>,
    ) {
      const { row, parentId } = action.payload;
      if (!parentId) {
        const index = state.services.singleServices?.findIndex(
          (item) => item.id === row.id,
        );
        if (
          index !== undefined &&
          index !== -1 &&
          state.services.singleServices
        ) {
          state.services.singleServices[index] = row;
        }
        state.selectedService = row;
      } else {
        const prices = state.options?.prices as any;
        const activeStep = state.steps.find((item) => item.active);
        const fieldsValues = activeStep?.fieldsValues;
        const pricesInStep = fieldsValues?.prices as any;

        if (prices) {
          const parent = prices.find((price: any) => price.id === parentId);

          if (parent && parent.active_price?.id === row.id) {
            parent.active_price = row;

            parent.prices =
              row.price_data?.data?.reduce<number[]>((acc, item) => {
                if (item.price != null) {
                  acc.push(Number(item.price));
                }
                return acc;
              }, []) || [];
          }
        }

        if (pricesInStep) {
          const parent = pricesInStep.find(
            (price: any) => price.id === parentId,
          );

          if (parent && parent.active_price?.id === row.id) {
            parent.active_price = row;

            parent.prices =
              row.price_data?.data?.reduce<number[]>((acc, item) => {
                if (item.price != null) {
                  acc.push(Number(item.price));
                }
                return acc;
              }, []) || [];
          }
        }
      }
    },
    setPackageField(
      state,
      action: PayloadAction<{
        pkg: MacrosServiceType;
        service?: SingleServiceType;
      }>,
    ) {
      const { pkg, service } = action.payload;

      if (service) {
        const macros = state.macros.macrosServices;
        if (!macros) return;

        const pkgIndex = macros.findIndex((item) => item.id === pkg.id);
        if (pkgIndex === -1) return;

        const activePrices = macros[pkgIndex].active_prices;
        const serviceIndex = activePrices.findIndex((s) => s.id === service.id);
        if (serviceIndex === -1) return;

        activePrices[serviceIndex] = service;
      } else {
        state.selectedPackage = pkg;
      }
    },
    cleanServiceField(state) {
      state.selectedService = null;
    },
    cleanPackageField(state) {
      state.selectedPackage = null;
    },
    cleanMacros(state) {
      state.macros.macrosServices = null;
    },
    cleanServices(state) {
      state.services.singleServices = null;
    },
    setStepsServicesValidates(state, action: PayloadAction<any>) {
      if (state.options?.prices) {
        state.options.prices = state.options.prices.map((price: any) => {
          const validated = action.payload.find(
            (s: any) => s.active_price.id === price.active_price?.id,
          );
          if (validated && price.active_price) {
            return {
              ...price,
              active_price: {
                ...price.active_price,
                is_valid: validated.active_price.is_valid,
              },
              passive_prices: price.passive_prices?.map((p: any) => {
                const validatedPassive = validated.passive_prices?.find(
                  (vp: any) => vp.id === p.id,
                );
                return validatedPassive
                  ? { ...p, is_valid: validatedPassive.is_valid }
                  : p;
              }),
            };
          }
          return price;
        });
      }

      const addServicesStep = state.steps.find((s) => s.id === "addServices");
      if (addServicesStep && state.options?.prices) {
        addServicesStep.fieldsValues = {
          ...addServicesStep.fieldsValues,
          prices: state.options.prices,
        };
        addServicesStep.validated = validateStepFields(
          addServicesStep.fieldsValues,
        );
      }
    },
  },
});

export const selectSteps = (state: RootState) => state.steps;
export const flowType = (state: RootState) => state.steps.type;

export const modifiedOfferId = createSelector(
  [selectSteps],
  (steps) => steps.offer_id,
);
export const getSuppliersPriceList = createSelector(
  [selectSteps],
  (steps) => steps.suppliers,
);

export const selectedService = createSelector(
  [selectSteps],
  (steps) => steps.selectedService,
);

export const selectedPackage = createSelector(
  [selectSteps],
  (options) => options?.selectedPackage,
);

export const stepsOptions = createSelector(
  [selectSteps],
  (steps) => steps.options,
);

export const selectedServices = createSelector(
  [stepsOptions],
  (options) => options?.prices,
);

export const hasInvalidSelectedServices = createSelector(
  [selectedServices],
  (services) =>
    services?.some(
      (service) =>
        service.active_price?.is_valid === false ||
        service.passive_prices?.some((price) => price.is_valid === false),
    ),
);

export const singleServices = createSelector(
  [selectSteps],
  (steps) => steps.services.singleServices,
);

export const passiveServices = createSelector(
  [selectSteps],
  (steps) => steps.suppliers.services,
);

export const selectActiveStep = createSelector(
  [selectSteps],
  (steps) => steps.steps.find((step: StepType) => step.active) || null,
);
export const selectActiveStepFields = createSelector(
  [selectActiveStep],
  (step) => step?.fieldsValues,
);
export const selectIsLastStep = createSelector(
  [selectSteps, selectActiveStep],
  (steps, activeStep) => {
    if (!activeStep) return false;
    return steps.steps[steps.steps.length - 1].id === activeStep.id;
  },
);
export const getValidityStart = createSelector(
  [selectSteps],
  (steps) => steps.options?.validity_start,
);

export const getCustomer = createSelector(
  [selectSteps],
  (steps) => steps.options?.customer,
);

export const getOfferStatus = (state: RootState) => state.steps.offer?.status;

export const {
  stepsInit,
  setNextStep,
  setPrevStep,
  cancelOffer,
  cleanServices,
  cleanMacros,
  hideWarning,
  getOfferStart,
  getOfferSuccess,
  getOfferFailure,
  setNotFoundError,
  setStepValidated,
  getServicesStart,
  submitOfferSuccess,
  getServicesSuccess,
  getServicesFailure,
  getMacrosStart,
  getMacrosSuccess,
  getMacrosFailure,
  getSuppliersListStart,
  getSuppliersListSuccess,
  getSuppliersListFailure,
  getSupplierMarginStart,
  getSupplierMarginSuccess,
  getSupplierMarginFailure,
  getAddedServicesSuccess,
  setServiceField,
  setPackageField,
  cleanServiceField,
  cleanPackageField,
  setStepsServicesValidates,
} = steps.actions;

export default steps.reducer;

export const fetchOffer =
  (params: {
    id: number;
    type: string;
    navigate?: (arg0: string) => void;
    url?: string;
  }): AppThunk =>
  async (dispatch) => {
    const { url, navigate, id } = params;
    try {
      dispatch(getOfferStart());
      const offer = await getOfferApi(id);
      if (url && navigate) {
        navigate(url);
      }

      if (["edit", "duplicate"].includes(params.type)) {
        const normalized = normalizeOfferResponse(offer);
        dispatch(
          fetchOfferServices(Number(id), {
            ...normalized,
            size: 1000,
            page: 1,
          }),
        );
      }
      dispatch(getOfferSuccess({ offer, type: params.type }));
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        dispatch(setNotFoundError(`Offer ${id} not found`));
      } else {
        const error = getErrorMessageFromResponse(err);
        dispatch(getOfferFailure(error));
      }
    }
  };

export const fetchSingleServices =
  (params: RequestActivePricesParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getServicesStart());
      const data = await getActivePricesApi(params);
      dispatch(getServicesSuccess({ services: data.items, total: data.total }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getServicesFailure(error));
    }
  };

export const fetchMacrosServices =
  (params: RequestActivePricesParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getMacrosStart());
      const data = await getMacrosPricesApi(params);
      dispatch(getMacrosSuccess({ services: data.items, total: data.total }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getMacrosFailure(error));
    }
  };

export const fetchSuppliersList =
  (params: RequestActivePricesParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getSuppliersListStart());
      const data = await getPassivePricesApi(params);
      dispatch(
        getSuppliersListSuccess({ items: data.items, total: data.total }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getSuppliersListFailure(error));
    }
  };

export const fetchSupplierMargin =
  (params: RequestMarginParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getSupplierMarginStart());
      const data = await calculateMarginApi(params);
      dispatch(
        getSupplierMarginSuccess({
          margins: data,
          supplierId: params.passive_price_id,
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getSupplierMarginFailure(error));
    }
  };

export const submitOfferCreation =
  (
    options: CreateOfferFieldsType,
    navigate: (path: string) => void,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getOfferStart());
      const data = await createOfferApi(options);
      navigate(`/offers/${data.id}`);
      setTimeout(() => {
        dispatch(cancelOffer());
        dispatch(submitOfferSuccess("The offer has been successfully created"));
      }, 1000);
    } catch {
      dispatch(
        getOfferFailure("The offer has not been created, please try again"),
      );
    }
  };

export const updateOffer =
  (
    id: number,
    options: CreateOfferFieldsType,
    navigate: (path: string) => void,
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getOfferStart());
      const data = await updateOfferApi(id, options);
      navigate(`/offers/${data.id}`);
      setTimeout(() => {
        dispatch(cancelOffer());
        dispatch(
          submitOfferSuccess("The offer has been successfully modified"),
        );
      }, 1000);
    } catch {
      dispatch(
        getOfferFailure("The offer has not been modified, please try again"),
      );
    }
  };
